import cn from 'classnames'
import { Flex, Spin } from 'antd'
import { isEmpty } from 'lodash-es'
import { LoadingOutlined } from '@ant-design/icons'
import { ImageControls, SelectType } from '../ChooseImageModal/types'
import s from './s.module.css'
import Controls from './Controls'
import { ImageProp } from './types'
import { checkImageAsset } from './utils'
import LazyLoadImage from '@/components/LazyLoadImage'

type Props<T extends ImageProp> = {
  img: T
  muteNotSelected?: boolean
  isSelected?: boolean
  id?: string
  controls?: ImageControls<T>
  selectType?: SelectType
  isProcessing?: boolean
  sizes?: string
  isSquare?: boolean
}

// eslint-disable-next-line complexity
export default function ImageContent<T extends ImageProp>({
  img,
  selectType,
  muteNotSelected,
  isSelected,
  id,
  controls,
  isProcessing,
  sizes,
  isSquare,
}: Props<T>) {
  const hasControls = !isProcessing && !isEmpty(controls)
  const hasHoverControls = hasControls && !!(controls?.onSetDefault || controls?.onRemove || controls?.onUnsetDefault)
  const showCheckbox = hasControls && controls?.onSelect && selectType === 'checkbox'
  const isImageAsset = checkImageAsset(img)
  const src = isImageAsset ? img.url : img.thumbUrl
  const isDefault = isImageAsset ? img.is_default : false

  let availableControls: ImageControls<T> = {}
  if (isDefault) {
    availableControls = { ...controls, onSetDefault: undefined, onRemove: undefined }
  } else {
    availableControls = { ...controls, onUnsetDefault: undefined }
  }

  return (
    <div
      onClick={() => controls?.onSelect?.(img)}
      className={cn(s.imgWrap, {
        [s.border]: selectType === 'border',
        [s.borderSelected]: !showCheckbox && isSelected,
        [s.withHoverControls]: hasHoverControls,
        [s.square]: isSquare,
      })}
    >
      {/** todo make alt for ImageAsset to name when backend will return name property */}
      <LazyLoadImage
        srcSet={isImageAsset ? img.srcSet : undefined}
        src={src}
        sizes={sizes}
        className={cn(s.image, { [s.muted]: (muteNotSelected && !isSelected) || isProcessing })}
        id={id}
        alt={isImageAsset ? 'image' : img?.fileName}
        isSquare={isSquare}
      />
      {isProcessing && (
        <Flex className={s.processingSpin} align="center" justify="center">
          <Spin indicator={<LoadingOutlined />} />
        </Flex>
      )}
      {hasControls && (
        <Controls
          hasHoverControls={hasHoverControls}
          controls={availableControls}
          img={img}
          isSelected={isSelected}
          showCheckbox={showCheckbox}
        />
      )}
    </div>
  )
}
